
import React from 'react';
import styled from '@emotion/styled';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Edit3 } from 'lucide-react';

interface CombinedImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  combinedImageUrl: string;
  productName: string;
  productColor: string;
  productGender: string;
  onSave: () => void;
  onEdit: () => void;
  isSaving: boolean;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
`;

const PreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
`;

const ProductInfo = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  }
`;

const EditButton = styled(Button)`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(135deg, #4338ca 0%, #3730a3 100%);
  }
`;

const CombinedImagePreviewModal: React.FC<CombinedImagePreviewModalProps> = ({
  isOpen,
  onClose,
  combinedImageUrl,
  productName,
  productColor,
  productGender,
  onSave,
  onEdit,
  isSaving
}) => {
  const handleEdit = () => {
    onEdit();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Preview da Imagem Combinada</DialogTitle>
        </DialogHeader>
        
        <ModalContent>
          <PreviewContainer>
            <PreviewImage 
              src={combinedImageUrl} 
              alt="Preview da imagem combinada"
            />
          </PreviewContainer>
          
          <ProductInfo>
            {productName} {productGender === 'male' ? 'Masculina' : 'Feminina'} - Cor: {productColor}
          </ProductInfo>
          
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
            Esta é uma prévia de como ficará sua imagem aplicada no produto.
            <br />
            Você pode salvar se estiver satisfeito ou editar para fazer ajustes.
          </div>
          
          <ButtonGroup>
            <EditButton onClick={handleEdit} variant="outline">
              <Edit3 className="h-4 w-4 mr-2" />
              Editar Design
            </EditButton>
            
            <SaveButton onClick={onSave} disabled={isSaving}>
              <Check className="h-4 w-4 mr-2" />
              {isSaving ? 'Salvando...' : 'Salvar na Loja'}
            </SaveButton>
          </ButtonGroup>
        </ModalContent>
      </DialogContent>
    </Dialog>
  );
};

export default CombinedImagePreviewModal;
